import { createContext, useContext } from "react";

import { classed } from "@/components/classed.config";
import cn from "@/helpers/classNames";

import { ComponentProps, deriveClassed } from "@tw-classed/react";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
} from "lucide-react";

const AlertContainer = classed.div(
  "flex w-full items-center rounded-xs px-4 py-3 text-sm",
  {
    variants: {
      status: {
        info: "bg-blue-50 dark:bg-blue-400/20",
        success: "bg-green-100 dark:bg-green-900",
        warning: "bg-amber-100 dark:bg-amber-600/30",
        error: "bg-red-100 dark:bg-red-400/20",
        neutral: "border bg-black/2.5 dark:bg-white/5",
      },
    },
    defaultVariants: {
      status: "info",
    },
  },
);

const AlertIconBase = classed.span("mr-3 flex shrink-0 items-center", {
  variants: {
    status: {
      info: "text-blue-500",
      success: "text-green-500",
      warning: "text-yellow-600",
      error: "text-red-500",
      neutral: "text-gray-400",
    },
  },
});

type AlertStatus = "info" | "success" | "warning" | "error" | "neutral";

type IconComponent = React.ComponentType<{ className?: string }>;

const StatusToIconDict: Record<AlertStatus, IconComponent> = {
  info: InfoIcon,
  success: CircleCheckIcon,
  warning: CircleAlertIcon,
  error: TriangleAlertIcon,
  neutral: InfoIcon,
};

export function getStatusIcon(status: AlertStatus) {
  return StatusToIconDict[status];
}

const AlertContext = createContext<AlertStatus>("info");

export const Alert = deriveClassed<
  typeof AlertContainer,
  ComponentProps<typeof AlertContainer>
>(({ status = "info", children, ...restProps }, ref) => {
  return (
    <AlertContext.Provider value={status}>
      <AlertContainer
        ref={ref}
        status={status}
        {...(restProps as any)}
        className={cn(
          "flex w-full items-center px-4 py-3 text-sm",
          restProps.className,
        )}
      >
        {children}
      </AlertContainer>
    </AlertContext.Provider>
  );
});

export const AlertTitle = classed.div("mr-2 font-semibold");

export const AlertIcon = deriveClassed<
  typeof AlertIconBase,
  ComponentProps<typeof AlertIconBase> & {
    icon?: IconComponent;
  }
>(({ icon: iconProps, ...props }, ref) => {
  const status = useContext(AlertContext);
  const Icon = iconProps ?? getStatusIcon(status);
  return (
    <AlertIconBase ref={ref} status={status} {...props}>
      <Icon className="size-6" />
    </AlertIconBase>
  );
});
